import { Injectable, OnDestroy } from '@angular/core';

import { IRoomGeometry } from '@stair-craft/common/util';
import { Intersection, Mesh, Object3D, Plane, Raycaster, Vector2, Vector3 } from 'three';

import { SceneService } from './scene.service';

export interface IOpeningDragEvent {
    openingX: number;
    openingZ: number;
}

export interface IStairConstraints {
    stairWidth: number;
    totalRun: number;
}

@Injectable()
export class OpeningDragService implements OnDestroy {
    #scene!: SceneService;
    #room!: IRoomGeometry;
    #constraints: IStairConstraints = { stairWidth: 900, totalRun: 4200 };
    #locked: boolean = false;
    // Пусто до attach(): сервис создаётся вместе с компонентом, а канвас приходит
    // только после первой отрисовки. Уничтожение может случиться и до неё.
    #canvas: HTMLCanvasElement | null = null;

    #isDragging: boolean = false;
    #dragOffset: Vector3 = new Vector3();
    #raycaster: Raycaster = new Raycaster();
    #pointer: Vector2 = new Vector2();
    #dragPlane: Plane = new Plane();
    #intersectPoint: Vector3 = new Vector3();
    #rafPending: boolean = false;
    #slabMesh: Mesh | null = null;

    #onDrag: ((event: IOpeningDragEvent) => void) | null = null;

    #boundPointerDown: (event: PointerEvent) => void = (event: PointerEvent) => {
        this.#onPointerDown(event);
    };
    #boundPointerMove: (event: PointerEvent) => void = (event: PointerEvent) => {
        this.#onPointerMove(event);
    };
    #boundPointerUp: () => void = () => {
        this.#onPointerUp();
    };

    public init(scene: SceneService, canvas: HTMLCanvasElement, room: IRoomGeometry, onDrag: (event: IOpeningDragEvent) => void): void {
        this.#scene = scene;
        this.#canvas = canvas;
        this.#room = room;
        this.#onDrag = onDrag;

        canvas.addEventListener('pointerdown', this.#boundPointerDown);
        canvas.addEventListener('pointermove', this.#boundPointerMove);
        canvas.addEventListener('pointerup', this.#boundPointerUp);
    }

    public updateRoom(room: IRoomGeometry): void {
        this.#room = room;
        this.#slabMesh = null;
    }

    public updateConstraints(constraints: IStairConstraints): void {
        this.#constraints = constraints;
    }

    public setLocked(locked: boolean): void {
        this.#locked = locked;
    }

    /** Канваса может не быть: сервис живёт с компонентом, а канвас приходит с attach(). */
    #setCursor(value: string): void {
        if (this.#canvas) {
            this.#canvas.style.cursor = value;
        }
    }

    #updatePointer(event: PointerEvent): boolean {
        if (!this.#canvas) {
            return false;
        }
        const rect: DOMRect = this.#canvas.getBoundingClientRect();
        this.#pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.#pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        return true;
    }

    #findSlabMesh(): Mesh | null {
        if (this.#slabMesh) {
            return this.#slabMesh;
        }
        // Сцены нет, пока не поднят рендерер: искать плиту не в чем.
        if (!this.#scene.scene) {
            return null;
        }
        this.#scene.scene.traverse((obj: Object3D) => {
            if (obj instanceof Mesh && obj.name === 'ceiling-slab') {
                this.#slabMesh = obj;
            }
        });
        return this.#slabMesh;
    }

    #onPointerDown(event: PointerEvent): void {
        if (event.button !== 0 || this.#locked) {
            return;
        }

        if (!this.#updatePointer(event) || !this.#scene.camera) {
            return;
        }
        this.#raycaster.setFromCamera(this.#pointer, this.#scene.camera);

        const slab: Mesh | null = this.#findSlabMesh();
        if (!slab) {
            return;
        }

        const intersects: Intersection[] = this.#raycaster.intersectObject(slab, false);
        if (intersects.length === 0) {
            return;
        }

        const hit: Vector3 = intersects[0].point;
        const room: IRoomGeometry = this.#room;

        const openingCenterX: number = room.openingX + room.openingWidth / 2;
        const openingCenterZ: number = room.openingZ + room.openingLength / 2;

        const distX: number = Math.abs(hit.x - openingCenterX);
        const distZ: number = Math.abs(hit.z - openingCenterZ);

        if (distX <= room.openingWidth / 2 + 100 && distZ <= room.openingLength / 2 + 100) {
            this.#isDragging = true;
            this.#dragOffset.set(hit.x - room.openingX, 0, hit.z - room.openingZ);
            this.#dragPlane.setFromNormalAndCoplanarPoint(new Vector3(0, 1, 0), new Vector3(0, room.floorHeight, 0));
            if (this.#scene.controls) {
                this.#scene.controls.enabled = false;
            }
            this.#setCursor('grabbing');
        }
    }

    #onPointerMove(event: PointerEvent): void {
        if (!this.#isDragging) {
            this.#updateHoverCursor(event);
            return;
        }

        if (this.#rafPending) {
            return;
        }
        this.#rafPending = true;

        requestAnimationFrame(() => {
            this.#rafPending = false;
            this.#processDrag(event);
        });
    }

    #processDrag(event: PointerEvent): void {
        if (!this.#updatePointer(event) || !this.#scene.camera) {
            return;
        }
        this.#raycaster.setFromCamera(this.#pointer, this.#scene.camera);

        if (!this.#raycaster.ray.intersectPlane(this.#dragPlane, this.#intersectPoint)) {
            return;
        }

        const room: IRoomGeometry = this.#room;

        let newX: number = this.#intersectPoint.x - this.#dragOffset.x;
        let newZ: number = this.#intersectPoint.z - this.#dragOffset.z;

        // Combined constraints: opening in room + stair fits
        const maxX: number = Math.min(room.roomWidth - room.openingWidth, room.roomWidth - this.#constraints.stairWidth);
        const minZ: number = Math.max(0, this.#constraints.totalRun - room.openingLength);
        const maxZ: number = room.roomLength - room.openingLength;

        newX = Math.max(0, Math.min(maxX, newX));
        newZ = Math.max(minZ, Math.min(maxZ, newZ));

        newX = Math.round(newX);
        newZ = Math.round(newZ);

        this.#onDrag?.({ openingX: newX, openingZ: newZ });
    }

    #updateHoverCursor(event: PointerEvent): void {
        if (this.#locked) {
            this.#setCursor('');
            return;
        }
        if (!this.#updatePointer(event) || !this.#scene.camera) {
            return;
        }
        this.#raycaster.setFromCamera(this.#pointer, this.#scene.camera);

        const slab: Mesh | null = this.#findSlabMesh();
        if (!slab) {
            return;
        }

        const intersects: Intersection[] = this.#raycaster.intersectObject(slab, false);
        if (intersects.length === 0) {
            this.#setCursor('');
            return;
        }

        const hit: Vector3 = intersects[0].point;
        const room: IRoomGeometry = this.#room;
        const openingCenterX: number = room.openingX + room.openingWidth / 2;
        const openingCenterZ: number = room.openingZ + room.openingLength / 2;

        const nearOpening: boolean =
            Math.abs(hit.x - openingCenterX) <= room.openingWidth / 2 + 100 &&
            Math.abs(hit.z - openingCenterZ) <= room.openingLength / 2 + 100;

        this.#setCursor(nearOpening ? 'grab' : '');
    }

    #onPointerUp(): void {
        if (this.#isDragging) {
            this.#isDragging = false;
            if (this.#scene.controls) {
                this.#scene.controls.enabled = true;
            }
            this.#setCursor('');
        }
    }

    public ngOnDestroy(): void {
        this.#canvas?.removeEventListener('pointerdown', this.#boundPointerDown);
        this.#canvas?.removeEventListener('pointermove', this.#boundPointerMove);
        this.#canvas?.removeEventListener('pointerup', this.#boundPointerUp);
    }
}
